"use client";

import { useEffect, useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
export default function Casestudytwocolumn() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const items = [{
    id: "pharma-1",
    number: "01",
    category: "PRECISÃO ESTÉRIL",
    title: "Revestimento de Comprimidos",
    description: "Os nossos sistemas aplicam revestimentos de forma uniforme, reduzindo a contaminação e o entupimento de bicos, para garantir a conformidade com os padrões de qualidade em processos em lote e contínuos.",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.16.png?v=yo37l4xqbqk",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.16.png?v=1xjdx1f1bvj-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.16.png?v=xp80xukn7ca-wvc-sizes",
    alt: "Linha de enchimento estéril de alta precisão em ambiente farmacêutico"
  }, {
    id: "pharma-2",
    number: "02",
    category: "REVESTIMENTO UNIFORME",
    title: "Limpeza de Recipientes",
    description: "A esterilidade e a facilidade de limpeza são da máxima importância na indústria farmacêutica. Ajudá-lo-emos a escolher a tecnologia de limpeza adequada para os seus recipientes.",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.21.png?v=1uagfw8ki44",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.21.png?v=7nf98ca5zvf-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.21.png?v=tqukc8ou7s-wvc-sizes",
    alt: "Sistema de pulverização e coating automatizado para comprimidos farmacêuticos"
  }, {
    id: "pharma-3",
    number: "03",
    category: "CONSULTORIA & I&D",
    title: "Secagem por Pulverização",
    description: "Oferecemos a mais ampla seleção de bicos de secagem por pulverização disponíveis, incluindo designs de núcleo, câmara de turbilhão (whirl chamber) e câmara de swirl, para todos os tipos de produtos secos por pulverização.",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.26.png?v=aw9lc11f9o8",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.26.png?v=fpl0d785h2-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-11-as-15.04.26.png?v=e3bgkpin774-wvc-sizes",
    alt: "Laboratório de I&D e suporte técnico para novos processos de pulverização"
  }];
  useEffect(() => {
    if (!sectionRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add({
        reduce: "(prefers-reduced-motion: reduce)",
        motion: "(prefers-reduced-motion: no-preference)"
      }, context => {
        const conditions = context.conditions as {
          reduce: boolean;
          motion: boolean;
        };
        if (conditions.reduce) {
          gsap.set("[data-reveal='intro'], [data-reveal='tile']", {
            opacity: 1,
            y: 0,
            clearProps: "all"
          });
          return;
        }
        gsap.from("[data-reveal='intro']", {
          opacity: 0,
          y: 24,
          duration: 0.7,
          ease: "cubic-bezier(0.22,0.61,0.36,1)",
          stagger: 0.08,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 82%"
          }
        });
        gsap.from("[data-reveal='tile']", {
          opacity: 0,
          y: 32,
          duration: 0.7,
          ease: "cubic-bezier(0.22,0.61,0.36,1)",
          stagger: 0.12,
          scrollTrigger: {
            trigger: "[data-grid='cards']",
            start: "top 85%"
          }
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return <section data-section-id="31406" ref={sectionRef} id="casestudytwocolumn" data-nav="dark" className="bg-background text-foreground relative overflow-hidden" style={{
    paddingBlock: "clamp(96px, 12vh, 168px)"
  }}>
      <div className="mx-auto w-full max-w-[1360px]" style={{
      paddingInline: "clamp(20px, 4vw, 56px)"
    }}>
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end">
          <div className="lg:col-span-7">
            <div data-reveal="intro" className="text-primary uppercase font-medium" style={{
            letterSpacing: "0.22em",
            fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
            lineHeight: 1
          }}>
              Soluções Farmacêuticas
            </div>
            <h2 data-reveal="intro" className="font-default font-medium text-foreground mt-6" style={{
            fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
            letterSpacing: "-0.03em",
            lineHeight: 1.05
          }}>
              Tecnologia de Pulverização Farmacêutica.
            </h2>
          </div>

          <div className="lg:col-span-5 lg:text-right">
            <p data-reveal="intro" className="text-muted-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
            letterSpacing: "-0.005em",
            lineHeight: 1.6
          }}>As operações de pulverização precisas são essenciais para a qualidade, a segurança e o potencial de rentabilidade de produtos farmacêuticos e de dispositivos médicos. Os principais fabricantes e investigadores de produtos farmacêuticos e de dispositivos médicos têm estabelecido parcerias com a Spraying Systems Co. para desenvolver novos produtos de pulverização que otimizam os seus processos e respondem aos seus objetivos de negócio.</p>
          </div>
        </div>

        {}
        <div data-grid="cards" className="mt-12 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {items.map((item, i) => <motion.div key={item.id} data-index={i} data-reveal="tile" whileHover={prefersReducedMotion ? undefined : {
          y: -6
        }} className="group relative flex flex-col justify-between rounded-3xl border border-border bg-card p-6 md:p-8 shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/40">
              <div className="flex flex-col">
                {}
                <div className="relative aspect-[3/2] w-full overflow-hidden rounded-xl bg-muted">
                  <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                </div>

                {}
                <div className="mt-6 flex items-center gap-2 text-xs font-mono font-medium tracking-wider text-muted-foreground uppercase">
                  <span>{item.number}</span>
                  <span>—</span>
                  <span>{item.category}</span>
                </div>

                {}
                <h3 className="mt-3 text-xl md:text-2xl font-bold tracking-tight text-card-foreground leading-snug">
                  {item.title}
                </h3>

                {}
                <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>)}
        </div>

        {}
        <div className="mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-8 border-t border-border">
          <p className="text-muted-foreground max-w-xl" style={{
          fontSize: "clamp(0.875rem, 0.95vw, 1rem)",
          lineHeight: 1.5
        }}>
            Cada solução Eurocontrol é desenhada para otimizar processos farmacêuticos, garantindo conformidade, eficiência e segurança operatória.
          </p>
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-13 rounded-lg gap-3 pl-7 pr-2 shrink-0" style={{
          height: "52px"
        }}>
            <span className="text-sm font-medium">Consulte-nos</span>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background text-primary transition-transform duration-300 group-hover:-rotate-45" aria-hidden="true">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </Button>
        </div>
      </div>
    </section>;
}